import { __, dummyArray } from '../../utils';
import FeedItem, { type IFeedItem } from '../FeedItem/FeedItem';
import FeedItemSkeleton from '../FeedItem/FeedItemSkeleton';
import { Link } from '../Link';
import {
  Button,
  CircularProgress,
  InfiniteScrollLoader,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Kde zoznam stál: karta, ktorá bola navrchu, a o koľko pixelov bola
 * odrolovaná za hornú hranu. Podľa karty, nie podľa `scrollTop`: kým bol
 * otvorený detail, mohol pribudnúť podnet a holé pixely by ukázali inam.
 * `top` je záloha pre zoznam bez kariet.
 */
export type FeedScrollPosition = {
  anchorId?: string;
  offset: number;
  top: number;
};

type Props = {
  items: IFeedItem[];
  isLoading: boolean;
  hideImage?: boolean;
  noFilterSelected?: boolean;
  updateFilters?: (newState) => void;
  onItemMapClick?: (itemId: string) => void;
  hasNoNewsPreferences?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  searchLocation?: string;
  /**
   * Zoznam bez vlastného plátna. V plávajúcom paneli na hlavnej stránke leží
   * na bielej karte, takže sivý podklad by v nej urobil druhú plochu.
   */
  plain?: boolean;
  /**
   * Pamäť pozície zoznamu, ktorá prežije jeho odmontovanie (moj-presov#24).
   * Otvorený detail vymení v paneli zoznam za detail, takže zoznam zaniká
   * a po návrate začínal navrchu. Pamäť drží stránka (`HomePageProvider`),
   * kľúčom je segment - podnety a novinky majú každý svoju pozíciu.
   */
  scrollStore?: Record<string, FeedScrollPosition | undefined>;
  scrollKey?: string;
};

const Feed: React.FC<Props> = ({
  isLoading,
  items,
  hideImage = false,
  noFilterSelected,
  onItemMapClick,
  hasNoNewsPreferences = false,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  searchLocation,
  plain = false,
  scrollStore,
  scrollKey,
}) => {
  const listRef = React.useRef<HTMLDivElement>(null);

  // Nové miesto na mape = nový zoznam, takže ide navrch a stará pozícia
  // neplatí. Pri vzniku zoznamu nie: to je návrat k nemu, nie zmena miesta,
  // a pozíciu obnovuje efekt nižšie. Porovnáva sa s predošlou hodnotou, nie
  // príznakom prvého behu - StrictMode efekt pri vzniku spúšťa dvakrát
  // a druhý beh by uloženú pozíciu zmazal.
  const previousLocation = React.useRef(searchLocation);
  React.useEffect(() => {
    if (previousLocation.current === searchLocation) return;
    previousLocation.current = searchLocation;
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
    if (scrollStore && scrollKey) {
      delete scrollStore[scrollKey];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLocation]);

  /**
   * Obnovenie pozície po návrate. Čaká, kým sú karty na svete - pri
   * prvom vykreslení môžu byť ešte dáta z dotazu na iný segment alebo
   * načítavanie, a obnovuje sa raz: hneď ako sa karta nájde, alebo ako
   * používateľ sám pohne zoznamom.
   */
  const isRestored = React.useRef(false);
  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (isRestored.current || isLoading || !items.length || !list) return;

    // Miesto dotazu sa tu neporovnáva: ak sa feed počas detailu prenačítal,
    // je to stále „ten zoznam, z ktorého som odišiel", a karta navrchu
    // v ňom spravidla je. Keď v ňom nie je, zoznam začne navrchu.
    const saved = scrollStore && scrollKey ? scrollStore[scrollKey] : undefined;
    if (!saved) {
      isRestored.current = true;
      return;
    }

    if (!saved.anchorId) {
      isRestored.current = true;
      list.scrollTop = saved.top;
      return;
    }

    const anchor = list.querySelector<HTMLElement>(
      `[data-feed-item="${CSS.escape(saved.anchorId)}"]`,
    );
    // Karta nemusí byť v prvom vykreslení (dáta iného segmentu počas
    // načítania) - skúša sa znova s ďalšími položkami, kým používateľ sám
    // nepohne zoznamom.
    if (!anchor) return;

    isRestored.current = true;
    const listTop = list.getBoundingClientRect().top;
    list.scrollTop =
      anchor.getBoundingClientRect().top -
      listTop +
      list.scrollTop +
      saved.offset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, items]);

  // Ukladá sa na skrolovanie, nie pri odmontovaní - vtedy už nemusí byť
  // čo merať. Raz za snímok, skrolovanie hlási udalosti častejšie.
  const saveFrame = React.useRef<number | null>(null);
  React.useEffect(
    () => () => {
      if (saveFrame.current !== null) cancelAnimationFrame(saveFrame.current);
    },
    [],
  );
  const handleScroll = () => {
    isRestored.current = true;
    if (!scrollStore || !scrollKey || saveFrame.current !== null) return;
    saveFrame.current = requestAnimationFrame(() => {
      saveFrame.current = null;
      const list = listRef.current;
      if (!list) return;
      const listTop = list.getBoundingClientRect().top;
      let anchorId: string | undefined;
      let offset = 0;
      for (const card of list.querySelectorAll<HTMLElement>(
        '[data-feed-item]',
      )) {
        const rect = card.getBoundingClientRect();
        if (rect.bottom > listTop) {
          anchorId = card.dataset.feedItem;
          offset = listTop - rect.top;
          break;
        }
      }
      scrollStore[scrollKey] = {
        anchorId,
        offset,
        top: list.scrollTop,
      };
    });
  };

  return (
    <Stack
      sx={{
        height: '100%',
        overflow: 'auto',
      }}
      ref={listRef}
      onScroll={handleScroll}
      // Značka pre gesto spodného listu: podľa nej vie zistiť, či je zoznam
      // odrolovaný hore, a teda či ťah nadol znamená „zavri", alebo len
      // skrolovanie v zozname.
      data-feed-scroll=''
      gap={1}
      bgcolor={plain ? 'transparent' : '#EDEFF1'}
    >
      {!isLoading && !items.length ? (
        <Stack
          sx={{
            py: 3,
            alignItems: 'center',
            gap: 1,
            px: 2,
          }}
        >
          {noFilterSelected ? (
            <Typography
              variant='body2'
              fontStyle='italic'
              color='textSecondary'
              sx={{
                maxWidth: 320,
                textAlign: 'center',
              }}
            >
              {__('Nie je vybraný žiaden filter.')}
            </Typography>
          ) : hasNoNewsPreferences ? (
            <Stack alignItems='center' gap={1}>
              <Typography
                variant='body2'
                fontStyle='italic'
                color='textSecondary'
                sx={{
                  maxWidth: 320,
                  textAlign: 'center',
                }}
              >
                {__(
                  'Momentálne sa nezaujímate o žiadne novinky. Kategórie noviniek si viete pridať v nastaveniach.',
                )}
              </Typography>
              <Link to='/profil/novinky'>
                <Button>{__('Prejsť do nastavení')}</Button>
              </Link>
            </Stack>
          ) : (
            <Typography
              variant='body2'
              fontStyle={'italic'}
              color='textSecondary'
            >
              {__('Neboli nájdené žiadne položky')}
            </Typography>
          )}
        </Stack>
      ) : (
        <>
          {isLoading && (
            <Stack
              alignItems={'center'}
              justifyContent={'center'}
              paddingTop={5}
            >
              <CircularProgress
                size={50}
                sx={{
                  color: 'primary.light',
                }}
              />
            </Stack>
          )}
          {!isLoading &&
            items &&
            items.map((item, index) => (
              <FeedItem
                key={`${index}-${item.id}`}
                showImage={!hideImage}
                item={item}
                onClick={onItemMapClick}
              />
            ))}
          {hasNextPage && fetchNextPage && (
            <InfiniteScrollLoader
              hasMore={hasNextPage}
              isLoading={isLoading || isFetchingNextPage}
              onVisible={fetchNextPage}
              targetRef={listRef}
            />
          )}

          {isFetchingNextPage &&
            dummyArray(2).map((idx) => <FeedItemSkeleton key={idx} />)}
        </>
      )}
    </Stack>
  );
};

export default Feed;
